import { useTiming } from "@app/hooks/useTiming";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FormatDateValue } from "./DateInput";
import { Trash } from "./SVGs";

export interface ProjectTime {
	run_time: number; // sum of all previous start-end / start-pause times and overrides below in milliseconds (1s = 1000ms)
	override_hours?: number; // override hours value set by user
	override_minutes?: number; // override minutes value set by user
	link_id: number; // the capsule id of the project for url nav
	activity: string;
	work_desc: string;
	requisitioner?: string;
	work_date: string;
	override_minimum: boolean;
}

export function NewProjectTime(id: number) {
	return {
		run_time: 0,
		link_id: id,
		activity: "Service",
		work_date: FormatDateValue(
			new Date().toLocaleDateString("en-AU", {
				hour12: false,
				timeZone: "Australia/Sydney",
			}) +
				"T" +
				new Date().toLocaleTimeString("en-AU", {
					hour12: false,
					timeZone: "Australia/Sydney",
				}),
		),
		work_desc: "",
		override_minimum: false,
	} as ProjectTime;
}

export function NewProjectTimeForDelete(id: number, minutes: number) {
	const t = NewProjectTime(id);
	// To update the project, we need to send negative minutes in milliseconds (60s per minutes, 1000ms per second)
	t.run_time = -1 * minutes * 60 * 1000;
	// To ensure the entry does not override to 15minutes, we need to flag override_minimum
	t.override_minimum = true;
	return t;
}

export function NewProjectTimeForEdit(id: number, minutes: number) {
	const t = NewProjectTime(id);
	// We need to send the DIFF in milliseconds (60s per minutes, 1000ms per second)
	t.run_time = minutes * 60 * 1000;
	// To ensure the entry does not override to 15minutes, we need to flag override_minimum
	t.override_minimum = true;
	return t;
}

export function GetSetProjectTime(
	ref: string,
	id: number,
	t: Timing,
): ProjectTime {
	let projectTime = t.projects[ref];

	if (projectTime == null) {
		projectTime = NewProjectTime(id);
	}

	t.projects[ref] = projectTime;

	return projectTime;
}

export interface Timing {
	active_project: string | null; // project_id
	start_time: number | null; // most recent start/resume time. Null if paused
	projects: { [p: string]: ProjectTime };
}

export function NewTiming(): Timing {
	const projects: { [p: number]: ProjectTime } = {};
	return {
		active_project: null,
		start_time: null,
		projects: projects,
	};
}

export function ReadTiming(): Timing {
	const result = localStorage.getItem("timing");
	if (result) {
		return JSON.parse(result) as Timing;
	} else {
		return NewTiming();
	}
}

export function SaveTiming(t: Timing) {
	localStorage.setItem("timing", JSON.stringify(t));
}

export function ToggleTimer(
	newProjectID: string,
	linkID: number,
	t: Timing,
): Timing {
	const now = Date.now();
	let projectRunTimes = GetSetProjectTime(newProjectID, linkID, t);
	const timerRunning = t.start_time != null;

	// derive desired action from state
	const resuming = !timerRunning;
	const pausing = timerRunning && newProjectID === t.active_project;
	const switching = timerRunning && newProjectID !== t.active_project;

	// if resuming timing, or starting (if start time isn't set), return active_project as newProjectID and start_time as now
	if (resuming) {
		return {
			active_project: newProjectID,
			start_time: now,
			projects: t.projects,
		};
	}

	// if pausing, nullify active_project & start_time, and update the project's run_time
	if (pausing) {
		projectRunTimes = {
			...projectRunTimes,
			run_time: projectRunTimes.run_time + (now - (t.start_time || now)),
		};
		return {
			active_project: null,
			start_time: null,
			projects: {
				...t.projects,
				[newProjectID]: projectRunTimes,
			},
		};
	}

	// if switching, swap the active_project and update the run_time accordingly
	if (switching) {
		const from = t.active_project || 0;
		const to = newProjectID;

		let fromProjectTimes = t.projects[from];
		if (fromProjectTimes == null) {
			fromProjectTimes = NewProjectTime(linkID);
		}
		fromProjectTimes = {
			...fromProjectTimes,
			run_time: fromProjectTimes.run_time + (now - (t.start_time || now)),
		};

		let toProjectTimes = t.projects[to];
		if (toProjectTimes == null) {
			toProjectTimes = NewProjectTime(linkID);
		}

		return {
			active_project: newProjectID,
			start_time: now,
			projects: {
				...t.projects,
				[from]: fromProjectTimes,
				[to]: toProjectTimes,
			},
		};
	}

	return t;
}

export function ApplyTimingOverride(
	id: string,
	linkID: number,
	t: Timing,
	override: number,
	metric: "hours" | "minutes",
): Timing {
	const previousProject = GetSetProjectTime(id, linkID, t);

	switch (metric) {
		case "hours":
			previousProject.override_hours = override;
			break;
		case "minutes":
			previousProject.override_minutes = override;
			break;
	}

	t.projects[id] = previousProject;

	return {
		active_project: t.active_project,
		start_time: t.start_time,
		projects: t.projects,
	};
}

export function ClearTiming(id: string) {
	const t = ReadTiming();
	if (t != null) {
		delete t.projects[id];
	}
	t.active_project = null;
	t.start_time = null;
	SaveTiming(t);
}

function useNow(enabled: boolean, intervalMs = 1000) {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		if (!enabled) {
			return;
		}

		setNow(Date.now());

		const intervalId = window.setInterval(() => {
			setNow(Date.now());
		}, intervalMs);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [enabled, intervalMs]);

	return now;
}

export const TimingPanel = ({
	projectID,
	linkID,
	canSave,
	onSave,
	saving,
}: {
	projectID: string;
	linkID: number;
	canSave: boolean;
	onSave: (t: ProjectTime) => void;
	saving: boolean;
}) => {
	const [_, setFormState] = useState<number>(0);

	const timing = useTiming();
	const now = useNow(timing.data?.start_time != null);

	if (timing.data == null) {
		return null;
	}

	let timeLabel = "00:00:00";

	let projectTimeData = timing.data.projects[projectID];
	if (projectTimeData == null) {
		const sameLinkExists = Object.values(timing.data.projects).some(
			(p) => p.link_id === linkID,
		);

		if (timing.data.active_project === projectID || sameLinkExists) {
			Sentry.withScope((scope) => {
				scope.setLevel("warning");
				scope.setTag("component", "TimingPanel");
				scope.setFingerprint(["timing", "missing_project_time"]);
				scope.setContext("timing", {
					active_project: timing.data.active_project,
					start_time: timing.data.start_time,
					projectID,
					linkID,
					sameLinkExists,
				});
				Sentry.captureMessage(
					"Missing project time data for current project; creating new",
				);
			});
		}

		projectTimeData = NewProjectTime(linkID);
	}

	if (projectTimeData != null) {
		let totalTime = 0;
		if (timing.data.start_time != null) {
			totalTime += now - timing.data.start_time;
		}
		totalTime += projectTimeData.run_time;
		totalTime += (projectTimeData.override_hours || 0) * 60 * 60 * 1000;
		totalTime += (projectTimeData.override_minutes || 0) * 60 * 1000;
		totalTime /= 1000;
		const h = Math.floor((totalTime % (3600 * 24)) / 3600);
		const m = Math.floor((totalTime % 3600) / 60);
		const s = Math.floor(totalTime % 60);
		timeLabel =
			h.toFixed().padStart(2, "00") +
			":" +
			m.toFixed().padStart(2, "00") +
			":" +
			s.toFixed().padStart(2, "00");
	}

	const onCancelTimer = (id: string) => {
		ClearTiming(id);
		timing.refetch().then();
	};

	const onPauseResume = () => {
		if (timing.data != null) {
			SaveTiming(ToggleTimer(projectID, linkID, timing.data));
			timing.refetch().then();
		}
	};

	const onComplete = () => {
		const t = ToggleTimer(projectID, linkID, timing.data);
		SaveTiming(t);
		const completeProject = t.projects[projectID];
		if (completeProject) {
			onSave(completeProject);
		}
	};

	const timerRunning = timing.data.start_time != null;

	const timerNotStarted =
		projectTimeData.run_time === 0 && timing.data.start_time == null;

	const cannotCancel = !timerRunning && projectTimeData.run_time === 0;

	const otherTimers = Object.entries(timing.data.projects).filter(
		([id]) => id !== projectID,
	);

	return (
		<div className={"flex-col"}>
			<div key={"time-override"} className={"flex flex-row gap"}>
				<div className={"flex flex-col"}>
					<label htmlFor={"hours"}>Add Hours:</label>
					<input
						id={"hours"}
						title={"Hours"}
						value={projectTimeData.override_hours || ""}
						className={"input"}
						type={"number"}
						min={0}
						step={1}
						onChange={(e) => {
							setFormState((s) => s + 1);
							SaveTiming(
								ApplyTimingOverride(
									projectID,
									linkID,
									timing.data,
									Number(e.target.value),
									"hours",
								),
							);
							timing.refetch();
						}}
					/>
				</div>
				<div className={"flex flex-col"}>
					<label htmlFor={"minutes"}>Add Minutes:</label>
					<input
						id={"minutes"}
						title={"Minutes"}
						value={projectTimeData.override_minutes || ""}
						className={"input"}
						type={"number"}
						step={15}
						onChange={(e) => {
							setFormState((s) => s + 1);
							SaveTiming(
								ApplyTimingOverride(
									projectID,
									linkID,
									timing.data,
									Number(e.target.value),
									"minutes",
								),
							);
							timing.refetch();
						}}
					/>
				</div>
			</div>
			<div className={"flex-col center"}>
				<h1 className={"header-with-sub flex-row center"}>{timeLabel}</h1>
				<div className={"flex-row center"}>
					{
						<button
							type={"button"}
							className={"button button-primary"}
							onClick={() => onPauseResume()}
						>
							{timerNotStarted
								? "Start Timer"
								: !timerRunning
									? "Resume Timer"
									: "Pause Timer"}
						</button>
					}
					<button
						type={"button"}
						className={"button button-success"}
						disabled={!canSave || saving}
						onClick={() => {
							onComplete();
						}}
					>
						Complete
					</button>
					<button
						type={"button"}
						disabled={cannotCancel}
						className={"button button-danger"}
						onClick={() => onCancelTimer(projectID)}
					>
						Cancel
					</button>
				</div>
			</div>
			{otherTimers.length === 0 ? null : (
				<div className={"flex-col center"}>
					<h3>Other Project Timers</h3>
					{otherTimers.map(([id, times]) => {
						return (
							<TimeSummary
								projectID={id}
								key={id}
								projectTime={times}
								onCancelTimer={onCancelTimer}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
};

const TimeSummary = ({
	projectID,
	projectTime,
	onCancelTimer,
}: {
	projectID: string;
	projectTime: ProjectTime;
	onCancelTimer: (id: string) => void;
}) => {
	const [, setSearchParams] = useSearchParams();

	let totalTime = 0;
	totalTime += projectTime.run_time;
	totalTime /= 1000;

	const h = Math.floor((totalTime % (3600 * 24)) / 3600);
	const m = Math.floor((totalTime % 3600) / 60);
	const s = Math.floor(totalTime % 60);
	const timeLabel =
		h.toFixed().padStart(2, "00") +
		":" +
		m.toFixed().padStart(2, "00") +
		":" +
		s.toFixed().padStart(2, "00");

	return (
		<div className={"header-with-sub flex-row gap center"}>
			<button
				type={"button"}
				className={"button-div"}
				onClick={() =>
					setSearchParams({ project: projectTime.link_id.toFixed(0) })
				}
			>
				{projectID} : {timeLabel}
			</button>
			<button
				type={"button"}
				className={"button-div"}
				onClick={() => onCancelTimer(projectID)}
				title={"Delete Timer"}
			>
				<Trash />
			</button>
		</div>
	);
};
